import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import type { SeriesLaborRollup } from '@/lib/api/jobs';
import { fillTemplate, formatMoney, getInitials } from '@/lib/utils';

interface Props {
  labor: SeriesLaborRollup;
  perVisit: number;
}

export function LaborTile({ labor, perVisit }: Props) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const rows = labor.by_employee ?? [];

  return (
    <div
      className="col-span-2 sm:col-span-1 flex flex-col gap-1 rounded-xl border bg-card px-4 py-3"
      data-testid="series-tile-labor"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between text-left"
        data-testid="series-labor-toggle"
        disabled={rows.length === 0}
      >
        <span className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">{t.seriesLaborPerVisit}</span>
          <span className="text-lg font-semibold">
            {formatMoney(perVisit)}
          </span>
        </span>
        {rows.length > 0 &&
          (open ? (
            <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
          ) : (
            <div className="flex -space-x-1.5">
              {rows.slice(0, 3).map((r) => (
                <span
                  key={r.employee_id}
                  className="w-6 h-6 rounded-full bg-muted text-[10px] font-semibold flex items-center justify-center border border-card"
                >
                  {getInitials(r.full_name)}
                </span>
              ))}
              <ChevronRightIcon className="w-4 h-4 text-muted-foreground self-center ml-1" />
            </div>
          ))}
      </button>

      {open && (
        <ul className="flex flex-col gap-1 pt-1">
          {rows.map((r) => (
            <li
              key={r.employee_id}
              className="flex items-center justify-between text-xs"
              data-testid={`series-labor-row-${r.employee_id}`}
            >
              <span className="truncate">{r.full_name}</span>
              <span className="text-muted-foreground shrink-0 ml-2">
                {fillTemplate(t.seriesLaborVisits, { n: r.visits_assigned })} ·{' '}
                {formatMoney(r.total_pay)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
